import type { TrialPosture } from "@/lib/trial";

/**
 * When the credit meter turns over, and what to call that in the UI.
 *
 * Display-only mirror of the backend's `usage_credits_period_key` /
 * `creditsResetAt` (vernot `_shared/usage.ts`): credits reset monthly on the
 * tenant's BILLING day when `companies.credits_period_start` is stamped, and on
 * the UTC calendar 1st when it isn't. Same month arithmetic as the server,
 * clamping included, so the date shown is the date enforced.
 *
 * The part that isn't arithmetic: **a trial doesn't reset.** Its allowance runs
 * out with the window and nothing refills — dispatchers and brokers drop to 0
 * credits when the 14 days are up (see lib/trial.ts). Rendering "Resets in 21
 * days" over a trial promised a refill the server will never grant, which is
 * the bug this module exists to prevent.
 */

const DAY_MS = 86_400_000;

/** Month add with Postgres' end-of-month clamp (Jan 31 + 1 month = Feb 28). */
export function addMonthsUTC(d: Date, n: number): Date {
  const day = d.getUTCDate();
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
  const lastDay = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(day, lastDay));
  return t;
}

/** Whole months from the anchor to now — the backend's `age()` month count. */
function monthsElapsed(anchor: Date, now: Date): number {
  let months =
    (now.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - anchor.getUTCMonth());
  if (now.getUTCDate() < anchor.getUTCDate()) months -= 1;
  return Math.max(0, months);
}

/**
 * The next credit reset. With a billing anchor that's the next monthly
 * boundary measured from it (the renewal day); without one, the calendar 1st.
 */
export function nextCreditsResetAt(
  creditsPeriodStart: string | null | undefined,
  nowMs: number = Date.now(),
): Date {
  const now = new Date(nowMs);
  if (creditsPeriodStart) {
    const anchor = new Date(creditsPeriodStart);
    if (!Number.isNaN(anchor.getTime())) {
      return addMonthsUTC(anchor, monthsElapsed(anchor, now) + 1);
    }
  }
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/** Whole days until the next reset, rounded up, floored at 0. */
export function daysUntilCreditsReset(
  creditsPeriodStart: string | null | undefined,
  nowMs: number = Date.now(),
): number {
  return Math.max(0, Math.ceil((nextCreditsResetAt(creditsPeriodStart, nowMs).getTime() - nowMs) / DAY_MS));
}

export type CreditPeriodKind =
  /** Inside the no-card window: the allowance ends, it does not roll over. */
  | "trial"
  /** Window closed, nothing bought. */
  | "trial_ended"
  /** Monthly reset anchored to the tenant's billing day. */
  | "billing"
  /** Monthly reset on the calendar 1st (no billing anchor on file). */
  | "calendar";

export interface CreditPeriodDisplay {
  kind: CreditPeriodKind;
  /** Days until the boundary (reset, or trial end). Null when unknown. */
  days: number | null;
  /** The boundary itself. Null when a trial has no readable end date. */
  at: Date | null;
  /** "Aug 24" — the boundary, short form. Null when `at` is null. */
  dateLabel: string | null;
  /** Full sentence for the usage card. */
  line: string;
  /** Compact form for a page header / subtitle. Null when there's nothing worth saying. */
  short: string | null;
}

function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const days = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

/**
 * What to say about this tenant's credit period.
 *
 * `posture` comes from useTrialPosture (server-classified, same logic as
 * check_usage_limit), so the copy follows the same clock the enforcement does.
 */
/**
 * The one-clause version, for a banner or a tooltip where the full sentence
 * doesn't fit. Same rule: a trial's allowance ends, it doesn't refill.
 */
export function creditResetNote(args: {
  posture: TrialPosture;
  /** Whole days left in the trial window, when there is one. */
  trialDaysLeft?: number | null;
  /** Days until the next monthly reset (see daysUntilCreditsReset). */
  resetDays: number;
}): string {
  if (args.posture === "trial") {
    return typeof args.trialDaysLeft === "number"
      ? `Credits don't reset — your trial ends in ${args.trialDaysLeft}d.`
      : "Credits don't reset — they end with your trial.";
  }
  if (args.posture === "trial_expired") return "Your trial is over and its credits ended with it.";
  return `Resets in ${args.resetDays}d.`;
}

export function creditPeriodDisplay(args: {
  posture: TrialPosture;
  /** companies.trial_ends_at */
  trialEndsAt?: string | null;
  /** companies.credits_period_start — the billing anchor, when stamped. */
  creditsPeriodStart?: string | null;
  nowMs?: number;
}): CreditPeriodDisplay {
  const nowMs = args.nowMs ?? Date.now();

  if (args.posture === "trial") {
    const endsMs = args.trialEndsAt ? Date.parse(args.trialEndsAt) : NaN;
    if (Number.isFinite(endsMs)) {
      const at = new Date(endsMs);
      const left = Math.max(0, Math.ceil((endsMs - nowMs) / DAY_MS));
      return {
        kind: "trial",
        days: left,
        at,
        dateLabel: shortDate(at),
        line: `Trial credits — they don't reset. Your trial ends ${shortDate(at)} (${days(left)} left) and any credits left over end with it. Pick a plan to start a monthly allowance.`,
        short: `Trial ends ${shortDate(at)}`,
      };
    }
    return {
      kind: "trial",
      days: null,
      at: null,
      dateLabel: null,
      line: "Trial credits — they don't reset. Whatever is left ends with your trial. Pick a plan to start a monthly allowance.",
      short: "Trial credits",
    };
  }

  if (args.posture === "trial_expired") {
    return {
      kind: "trial_ended",
      days: null,
      at: null,
      dateLabel: null,
      line: "Your trial has ended, and its credits ended with it. Pick a plan to start a monthly allowance.",
      short: "Trial ended",
    };
  }

  const at = nextCreditsResetAt(args.creditsPeriodStart, nowMs);
  const left = Math.max(0, Math.ceil((at.getTime() - nowMs) / DAY_MS));
  // "on your billing date" is only true with an anchor on file — without one
  // the server really does reset on the calendar 1st, so don't claim otherwise.
  const anchored = !!args.creditsPeriodStart && !Number.isNaN(new Date(args.creditsPeriodStart).getTime());
  return {
    kind: anchored ? "billing" : "calendar",
    days: left,
    at,
    dateLabel: shortDate(at),
    line: anchored
      ? `Resets ${shortDate(at)}, on your billing date — in ${days(left)}. Updates in near real-time.`
      : `Resets ${shortDate(at)} — in ${days(left)}. Updates in near real-time.`,
    short: `Resets ${shortDate(at)}`,
  };
}
